/**
 * Multi-language code generation from an inferred schema.
 * Targets: TypeScript interfaces, Zod schemas, Pydantic v2 models,
 * PostgreSQL CREATE TABLE + INSERT. All output is copy-paste ready.
 */
import { camelCase, pascalCase, snakeCase } from './format';
import type { ColumnSchema, ColumnType, Row } from './types';

export type CodeTab = 'typescript' | 'zod' | 'pydantic' | 'postgres';

export const CODE_TABS: { id: CodeTab; label: string; blurb: string }[] = [
  { id: 'typescript', label: 'TypeScript', blurb: 'Strict interfaces with nullability from missing cells' },
  { id: 'zod', label: 'Zod', blurb: 'Runtime validators mirroring the inferred types' },
  { id: 'pydantic', label: 'Pydantic v2', blurb: 'Python models with EmailStr / HttpUrl where detected' },
  { id: 'postgres', label: 'PostgreSQL', blurb: 'CREATE TABLE plus INSERT literals for sample rows' },
];

export interface CodeMeta {
  tableName: string;
  columns: ColumnSchema[];
  sourceName?: string;
  rowCount?: number;
  rows?: Row[];
}

function tsType(t: ColumnType): string {
  switch (t) {
    case 'int':
    case 'float':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
}

function zodType(t: ColumnType, dateKind?: 'date' | 'datetime' | null): string {
  switch (t) {
    case 'int':
      return 'z.number().int()';
    case 'float':
      return 'z.number()';
    case 'boolean':
      return 'z.boolean()';
    case 'iso-date':
      return dateKind === 'date' ? 'z.string().date()' : 'z.string().datetime()';
    case 'email':
      return 'z.string().email()';
    case 'uuid':
      return 'z.string().uuid()';
    case 'url':
      return 'z.string().url()';
    default:
      return 'z.string()';
  }
}

function pyType(t: ColumnType, dateKind?: 'date' | 'datetime' | null): string {
  switch (t) {
    case 'int':
      return 'int';
    case 'float':
      return 'float';
    case 'boolean':
      return 'bool';
    case 'iso-date':
      return dateKind === 'date' ? 'date' : 'datetime';
    case 'email':
      return 'EmailStr';
    case 'uuid':
      return 'UUID';
    case 'url':
      return 'HttpUrl';
    default:
      return 'str';
  }
}

function sqlType(t: ColumnType, dateKind?: 'date' | 'datetime' | null): string {
  switch (t) {
    case 'int':
      return 'bigint';
    case 'float':
      return 'double precision';
    case 'boolean':
      return 'boolean';
    case 'iso-date':
      return dateKind === 'date' ? 'date' : 'timestamptz';
    case 'uuid':
      return 'uuid';
    default:
      return 'text';
  }
}

function sqlLiteral(t: ColumnType, value: string | null): string {
  if (value === null) return 'NULL';
  if (t === 'int' || t === 'float') {
    const n = Number(value.replace(/[$€£¥₹,\s]/g, ''));
    if (Number.isFinite(String(n)) && value.trim() !== '' && !Number.isNaN(n)) {
      // Keep the original literal when it already parses (avoids 1e21 rewrites).
      if (value.trim() === String(n)) return value.trim();
      return String(n);
    }
    return `'${value.replace(/'/g, "''")}'`;
  }
  if (t === 'boolean') {
    const s = value.trim().toLowerCase();
    if (['true', 'yes', 'y', 't', '1'].includes(s)) return 'TRUE';
    if (['false', 'no', 'n', 'f', '0'].includes(s)) return 'FALSE';
    return `'${value.replace(/'/g, "''")}'`;
  }
  return `'${value.replace(/'/g, "''")}'`;
}

function isPk(col: ColumnSchema, columns: ColumnSchema[]): boolean {
  if (!/^id$/i.test(col.key)) return false;
  if (col.type !== 'int' && col.type !== 'uuid') return false;
  if (col.nullCount > 0 || col.anomalyCount > 0) return false;
  void columns;
  return true;
}

function generateTypeScript(meta: CodeMeta): string {
  const { columns } = meta;
  if (columns.length === 0) return '// No columns detected.';
  const name = pascalCase(meta.tableName || 'imported_table');
  const lines: string[] = [];
  lines.push(`/** Auto-generated by ChaosMonkey CSV — ${columns.length} column(s). */`);
  lines.push(`export interface ${name} {`);
  for (const col of columns) {
    const nullable = col.nullCount > 0;
    const field = snakeCase(col.name);
    lines.push(`  /** ${col.name} — ${col.type} (${(col.confidence * 100).toFixed(1)}% match) */`);
    lines.push(`  ${field}: ${tsType(col.type)}${nullable ? ' | null' : ''};`);
  }
  lines.push('}');
  lines.push('');
  const keys = columns.map((c) => `  '${snakeCase(c.name)}'`).join(',\n');
  lines.push(`export const ${snakeCase(meta.tableName || 'imported_table').toUpperCase()}_KEYS = [`);
  lines.push(keys);
  lines.push('] as const;');
  return lines.join('\n');
}

function generateZod(meta: CodeMeta): string {
  const { columns } = meta;
  if (columns.length === 0) return '// No columns detected.';
  const schemaName = `${camelCase(meta.tableName || 'imported_table')}Schema`;
  const lines: string[] = [];
  lines.push(`// Auto-generated by ChaosMonkey CSV.`);
  lines.push(`import { z } from 'zod';`);
  lines.push('');
  lines.push(`export const ${schemaName} = z.object({`);
  for (const col of columns) {
    const nullable = col.nullCount > 0 ? '.nullable()' : '';
    lines.push(`  ${snakeCase(col.name)}: ${zodType(col.type, col.dateKind)}${nullable},`);
  }
  lines.push(`});`);
  lines.push('');
  lines.push(`export type ${pascalCase(meta.tableName || 'imported_table')} = z.infer<typeof ${schemaName}>;`);
  return lines.join('\n');
}

function generatePydantic(meta: CodeMeta): string {
  const { columns } = meta;
  if (columns.length === 0) return '# No columns detected.';
  const className = pascalCase(meta.tableName || 'imported_table');
  const needsEmail = columns.some((c) => c.type === 'email');
  const needsUrl = columns.some((c) => c.type === 'url');
  const needsDatetime = columns.some((c) => c.type === 'iso-date' && c.dateKind !== 'date');
  const needsDate = columns.some((c) => c.type === 'iso-date' && c.dateKind === 'date');
  const dateImports = [needsDate ? 'date' : null, needsDatetime ? 'datetime' : null].filter(Boolean);
  const pydantic = ['BaseModel', 'ConfigDict', needsEmail ? 'EmailStr' : null, needsUrl ? 'HttpUrl' : null].filter(
    Boolean,
  ) as string[];
  const lines: string[] = [];
  lines.push(`# Auto-generated by ChaosMonkey CSV (Pydantic v2).`);
  lines.push(`from __future__ import annotations`);
  lines.push(`from datetime import ${dateImports.length > 0 ? dateImports.join(', ') : 'datetime'}`);
  lines.push('');
  lines.push(`from pydantic import ${pydantic.join(', ')}`);
  lines.push('');
  lines.push('');
  lines.push(`class ${className}(BaseModel):`);
  lines.push(`    """Inferred from ${columns.length} column(s)."""`);
  lines.push('');
  lines.push(`    model_config = ConfigDict(from_attributes=True, str_strip_whitespace=True)`);
  lines.push('');
  for (const col of columns) {
    const t = pyType(col.type, col.dateKind);
    const field = snakeCase(col.name);
    if (col.nullCount > 0) lines.push(`    ${field}: ${t} | None = None`);
    else lines.push(`    ${field}: ${t}`);
  }
  return lines.join('\n');
}

function generatePostgres(meta: CodeMeta): string {
  const { columns } = meta;
  if (columns.length === 0) return '-- No columns detected.';
  const table = snakeCase(meta.tableName || 'imported_table');
  const pk = columns.find((c) => isPk(c, columns));
  const lines: string[] = [];
  lines.push(`-- Auto-generated by ChaosMonkey CSV.`);
  lines.push(`CREATE TABLE IF NOT EXISTS ${table} (`);
  columns.forEach((col, i) => {
    const field = snakeCase(col.name);
    let def: string;
    if (pk === col && col.type === 'int') def = `  ${field} bigserial PRIMARY KEY`;
    else if (pk === col) def = `  ${field} ${sqlType(col.type, col.dateKind)} PRIMARY KEY`;
    else def = `  ${field} ${sqlType(col.type, col.dateKind)}${col.nullCount === 0 ? ' NOT NULL' : ''}`;
    lines.push(i < columns.length - 1 ? `${def},` : def);
  });
  lines.push(');');
  const rows = (meta.rows ?? []).slice(0, 25);
  if (rows.length > 0) {
    lines.push('');
    lines.push(`-- ${rows.length} sample row(s).`);
    lines.push(`INSERT INTO ${table} (${columns.map((c) => snakeCase(c.name)).join(', ')})`);
    lines.push('VALUES');
    lines.push(rows.map((r) => `  (${columns.map((c, i) => sqlLiteral(c.type, r[i] ?? null)).join(', ')})`).join(',\n') + ';');
  }
  return lines.join('\n');
}

export function generateCode(tab: CodeTab, meta: CodeMeta): string {
  switch (tab) {
    case 'zod':
      return generateZod(meta);
    case 'pydantic':
      return generatePydantic(meta);
    case 'postgres':
      return generatePostgres(meta);
    case 'typescript':
    default:
      return generateTypeScript(meta);
  }
}
